<template>
  <div class="relative-date-picker">
    <Multiselect :value="refDate" label="Ref date" :options="refDateOptions" @input="$event => onRefDateChanged($event)" />
    <div class="offsets">
      <div v-for="(part, index) in offsets" :key="index" class="offset-row">
        <TextField :value="part.offset" label="Offset" type="number" @change="$event => onOffsetChanged(index, 'offset', $event)" />
        <Multiselect
          label="Unit"
          :options="unitOptions"
          :value="part.unit"
          @input="$event => onOffsetChanged(index, 'unit', $event)"
        />
        <Button v-if="offsets.length > 1" variant="text" @click="removeOffset(index)">
          <MdIcon name="close" />
        </Button>
      </div>
      <Button variant="text" @click="addOffset">
        <MdIcon name="plus" />
        Offset
      </Button>
    </div>
    <Multiselect
      :value="nextWeekdayDisplay"
      label="Next weekday"
      :options="weekdayOptions"
      @input="$event => onNextWeekdayChanged($event)"
    />
  </div>
</template>

<script>
import Button from '@/components/common/Button.vue';
import MdIcon from '@/components/common/MdIcon.vue';
import Multiselect from '@/components/common/Multiselect.vue';
import TextField from '@/components/common/TextField.vue';

const DEFAULT_OFFSET = { offset: 0, unit: 'day' };
const NONE_WEEKDAY = 'None';
const FILE_DATE_OPTION = 'File date';
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const BASE_REF_DATE_OPTIONS = [
  'Today',
  'Start of this week',
  'Start of this month',
  'Start of this year',
  'End of this week',
  'End of this month',
  'End of this year'
];

function normalizeOffsets(value) {
  if (value && Array.isArray(value.offsets) && value.offsets.length > 0) {
    return value.offsets.map(part => ({
      offset: part.offset != null ? part.offset : 0,
      unit: part.unit || 'day'
    }));
  }
  if (value && (value.unit != null || value.offset != null)) {
    return [
      {
        offset: value.offset != null ? value.offset : 0,
        unit: value.unit || 'day'
      }
    ];
  }
  return [{ ...DEFAULT_OFFSET }];
}

function normalizeNextWeekday(value) {
  if (value && value.nextWeekday && WEEKDAYS.includes(value.nextWeekday)) {
    return value.nextWeekday;
  }
  return null;
}

function buildRefDateOptions(allowFileDate, currentRefDate) {
  const options = [...BASE_REF_DATE_OPTIONS];
  if (allowFileDate || currentRefDate === FILE_DATE_OPTION) {
    options.push(FILE_DATE_OPTION);
  }
  return options;
}

export default {
  components: {
    Button,
    MdIcon,
    Multiselect,
    TextField
  },
  props: {
    value: {
      type: Object,
      default: () => null
    },
    allowFileDate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const currentRefDate = this.value ? this.value.refDate : BASE_REF_DATE_OPTIONS[0];
    return {
      refDate: currentRefDate,
      unitOptions: ['day', 'week', 'month', 'year'],
      weekdayOptions: [NONE_WEEKDAY, ...WEEKDAYS],
      offsets: normalizeOffsets(this.value),
      nextWeekday: normalizeNextWeekday(this.value)
    };
  },
  computed: {
    refDateOptions() {
      return buildRefDateOptions(this.allowFileDate, this.refDate);
    },
    nextWeekdayDisplay() {
      return this.nextWeekday || NONE_WEEKDAY;
    }
  },
  watch: {
    value(newValue) {
      if (!newValue) {
        return;
      }
      this.refDate = newValue.refDate;
      this.offsets = normalizeOffsets(newValue);
      this.nextWeekday = normalizeNextWeekday(newValue);
    }
  },
  created() {
    if (!this.value) {
      this.emitValue();
    }
  },
  methods: {
    emitValue() {
      const payload = {
        refDate: this.refDate,
        offsets: this.offsets.map(part => ({
          offset: Number(part.offset) || 0,
          unit: part.unit
        }))
      };
      if (this.nextWeekday) {
        payload.nextWeekday = this.nextWeekday;
      } else {
        payload.nextWeekday = null;
      }
      this.$emit('input', payload);
    },
    onRefDateChanged(value) {
      this.refDate = value;
      this.emitValue();
    },
    onOffsetChanged(index, field, value) {
      const next = this.offsets.slice();
      next[index] = { ...next[index], [field]: value };
      this.offsets = next;
      this.emitValue();
    },
    onNextWeekdayChanged(value) {
      this.nextWeekday = value === NONE_WEEKDAY ? null : value;
      this.emitValue();
    },
    addOffset() {
      this.offsets = [...this.offsets, { ...DEFAULT_OFFSET }];
      this.emitValue();
    },
    removeOffset(index) {
      if (this.offsets.length <= 1) {
        return;
      }
      this.offsets = this.offsets.filter((_, i) => i !== index);
      this.emitValue();
    }
  }
};
</script>

<style lang="scss" scoped>
.relative-date-picker {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 0 5px;
}

.offsets {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.offset-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  grid-gap: 5px;
  align-items: center;
}
</style>
